import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sidebar } from "./components/Sidebar";
import BottomTabBar from "./components/BottomTabBar";
import { CharacterLobby } from "./components/CharacterLobby";
import { CharacterCreator } from "./components/CharacterCreator";
import { ChatView } from "./components/ChatView";
import { ConversationHistory } from "./components/ConversationHistory";
import { OllamaSettingsPanel } from "./components/OllamaSettingsPanel";
import { MultiChatRoom } from "./components/MultiChatRoom";
import { LoginPage } from "./components/LoginPage";
import { AdminPanel } from "./components/AdminPanel";
import type { Character, ConversationSummary } from "./services/api";
import { getStatus, getLatestConversation, getToken, getStoredUser, removeToken, clearStoredUser, getGuestConversationCount, incrementGuestConversationCount, GUEST_CONV_THRESHOLD } from "./services/api";
import { Sparkles } from "lucide-react";

type NavView = "lobby" | "create" | "edit" | "messages" | "trainer" | "rooms" | "admin";

export default function App() {
  const [currentView, setCurrentView] = useState<NavView>("lobby");
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [editCharacter, setEditCharacter] = useState<Character | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [ollamaOnline, setOllamaOnline] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<{ username: string; role: string } | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [guestConversationCount, setGuestConversationCount] = useState(getGuestConversationCount());

  // 监听 BottomTabBar 触发的设置事件
  useEffect(() => {
    const handler = () => setIsSettingsOpen(prev => !prev);
    window.addEventListener("open-settings", handler);
    return () => window.removeEventListener("open-settings", handler);
  }, []);

  // 监听游客对话计数事件
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const count = incrementGuestConversationCount();
      setGuestConversationCount(count);
      if (count >= GUEST_CONV_THRESHOLD && !isLoggedIn) {
        setShowLoginPrompt(true);
      }
    };
    window.addEventListener("guest-conversation-started", handler);
    return () => window.removeEventListener("guest-conversation-started", handler);
  }, [isLoggedIn]);

  useEffect(() => {
    // 检查已有 token
    const token = getToken();
    const user = getStoredUser();
    if (token && user) {
      setIsLoggedIn(true);
      setCurrentUser(user);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      checkStatus();
      const timer = setInterval(checkStatus, 30000);
      return () => clearInterval(timer);
    }
  }, [isLoggedIn]);

  async function checkStatus() {
    try {
      const status = await getStatus();
      setOllamaOnline(status.ollama_online);
    } catch {}
  }

  function handleLogin(user: { username: string; role: string }) {
    setIsLoggedIn(true);
    setCurrentUser(user);
  }

  function handleLogout() {
    removeToken();
    clearStoredUser();
    setIsLoggedIn(false);
    setCurrentUser(null);
    setCurrentView("lobby");
  }

  function handleSelectCharacter(char: Character) {
    setSelectedCharacter(char);
    setConversationId(undefined);
    setIsChatOpen(true);
    // 异步获取最新对话 ID（不阻塞 UI 渲染）
    getLatestConversation(char.id).then(latestConv => {
      if (latestConv) setConversationId(latestConv.id);
    }).catch(() => {});
  }

  function handleSelectConversation(conv: ConversationSummary, char: Character | undefined) {
    // Fallback: if character not found (e.g. deleted or NSFW filtered), build a minimal character object
    const fallbackChar: Character = {
      id: conv.character_id,
      name: conv.character_name || "未知角色",
      description: "",
      avatar: conv.character_avatar || "",
      tags: [],
      category: "",
      gender: "female",
      personality: "",
      background: "",
      scenario: "",
      greeting: "",
      system_prompt: "",
      example_messages: [],
      nsfw: false,
      creator: "",
      creator_username: "",
      downloads: 0,
      conversations: 0,
      rating: 0,
      rating_count: 0,
      featured: false,
      created_at: conv.created_at || "",
      updated_at: conv.updated_at || "",
    };
    setSelectedCharacter(char || fallbackChar);
    setConversationId(conv.id);
    setIsChatOpen(true);
  }

  function handleCreated(char: Character) {
    setSelectedCharacter(char);
    setConversationId(undefined);
    setIsChatOpen(true);
  }

  function handleEditCharacter(char: Character) {
    setEditCharacter(char);
    // Set the edit view immediately underneath while chat exits
    setCurrentView("edit");
    setIsChatOpen(false);
  }

  function handleCharacterUpdated(char: Character) {
    setEditCharacter(null);
    setSelectedCharacter(char);
    setCurrentView("lobby");
  }

  function handleNavigate(view: NavView) {
    // When navigating away, close chat overlay
    setIsChatOpen(false);
    setSelectedCharacter(null);
    setConversationId(undefined);
    if (view !== "edit") {
      setEditCharacter(null);
    }
    setCurrentView(view);
  }

  function handleChatBack() {
    setIsChatOpen(false);
    // If we came from messages view, go back there
    // Otherwise go to lobby
  }

  function renderContent() {
    switch (currentView) {
      case "lobby":
        return <CharacterLobby onSelectCharacter={handleSelectCharacter} onEditCharacter={handleEditCharacter} />;
      case "create":
        return <CharacterCreator onBack={() => setCurrentView("lobby")} onCreated={handleCreated} />;
      case "edit":
        return (
          <CharacterCreator
            onBack={() => setCurrentView("lobby")}
            onCreated={handleCreated}
            onUpdated={handleCharacterUpdated}
            editCharacter={editCharacter}
          />
        );
      case "messages":
        return <ConversationHistory onSelectConversation={handleSelectConversation} />;
      case "trainer":
        return <OllamaSettingsPanel currentUser={currentUser} />;
      case "rooms":
        return <MultiChatRoom onBack={() => setCurrentView("lobby")} />;
      case "admin":
        return <AdminPanel onBack={() => setCurrentView("lobby")} />;
      default:
        return <CharacterLobby onSelectCharacter={handleSelectCharacter} />;
    }
  }

  return (
    <div className="h-screen w-screen flex bg-slate-950 overflow-hidden">
      <>
        {/* 桌面端侧边栏 - 移动端隐藏 */}
        <div className="hidden lg:flex">
          <Sidebar
            currentView={isChatOpen ? "chat" : currentView}
            onNavigate={handleNavigate}
            ollamaOnline={ollamaOnline}
            currentUser={currentUser}
            onLogout={handleLogout}
            guestConversationCount={guestConversationCount}
          />
        </div>

        {/* 主内容区域 - 移动端添加底部内边距 */}
        <main className="flex-1 h-full overflow-hidden relative pb-16 lg:pb-0">
          {/* Chat overlay */}
          <AnimatePresence>
            {isChatOpen && selectedCharacter && (
              <motion.div
                key={`chat-view-${selectedCharacter.id}-${conversationId || "new"}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 z-10 h-full"
              >
                <ChatView
                  character={selectedCharacter}
                  conversationId={conversationId}
                  onBack={handleChatBack}
                  onEditCharacter={handleEditCharacter}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentView}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="h-full"
            >
              {isSettingsOpen ? (
                <OllamaSettingsPanel currentUser={currentUser} onBack={() => setIsSettingsOpen(false)} />
              ) : (
                renderContent()
              )}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* 移动端底部标签栏 */}
        <BottomTabBar
          currentView={currentView}
          isChatOpen={isChatOpen}
          onNavigate={handleNavigate}
          onToggleSettings={() => setIsSettingsOpen(prev => !prev)}
        />
      </>

      {/* 登录提示弹窗（游客模式，20次对话后触发） */}
      <AnimatePresence>
        {showLoginPrompt && !isLoggedIn && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowLoginPrompt(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 20, stiffness: 200 }}
              className="bg-slate-900 border border-slate-700/50 rounded-2xl p-8 max-w-md w-full shadow-2xl shadow-purple-500/10"
            >
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500 via-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">
                  已对话 {guestConversationCount} 次！
                </h2>
                <p className="text-slate-400 text-sm leading-relaxed">
                  注册账号可保存你的对话历史、创建角色，解锁全部功能。
                </p>
              </div>

              <div className="space-y-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowLoginPrompt(false)}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-rose-500 to-purple-500 text-white font-medium transition-all hover:shadow-lg hover:shadow-purple-500/20"
                >
                  注册/登录（保存数据）
                </motion.button>
                <button
                  onClick={() => setShowLoginPrompt(false)}
                  className="w-full py-3 rounded-xl border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600 font-medium transition-all text-sm"
                >
                  暂不登录，继续使用
                </button>
              </div>

              <p className="text-center text-slate-600 text-xs mt-4">
                游客模式下对话记录仅保存在本地
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
